import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import { Row, Col, Button, Tooltip } from 'antd';
import { AvlKey, DeviceStatus, UnitOfMeasurement } from '../../../types/types';
import { fetchApiAuth } from '../../../core/utils/api';
import SpeedSign from '../../SpeedSign';
import Icon from '../../elements/Icon';
import Spinner from '../../Spinner';
import { CalendarHeatMap, Notification, Video, VideoChat } from '@carbon/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { setVideoSearchFilters } from '../../../core/video/videoActions';
import history from '../../../core/utils/history';
import { updateEventSearch } from '../../../core/global/globalActions';
import { RootState } from '../../../core/reducers';
import { getTimesheetReportCsvRequest } from '../../../core/report/reportActions';

import './assetPopup.scss';

interface AssetPopupResponse {
    act_as_tracker: 0 | 1;
    asset_picture?: string | null;
    driver: string;
    driver_score: number;
    marker: {
        data: AvlKey[];
        location: string;
        lat: string;
        lng: string;
        speed: number;
    };
    online_status: DeviceStatus;
    title: string;
    user_id: number;
    user_picture?: string | null;
}

/**
 * Get the color for the status icon based on device status.
 * @param deviceStatus 
 */
const getStatusIconColor = (deviceStatus: DeviceStatus): string => {
    switch (deviceStatus) {
        case DeviceStatus.Online:
            return 'var(--green-dark)';
        case DeviceStatus.Idling:
            return 'var(--yellow-dark)';
        case DeviceStatus.Stopped:
            return 'var(--red-dark)';
        case DeviceStatus.Offline:
            return 'var(--red-dark)';
        default:
            return '#ddd';
    }
}

interface AssetPopupProps {
    assetId: number;
    date: string;
    uomPref: UnitOfMeasurement;
    recordId: number;
    dvrImei?: string;
    hideQuickButtons?: boolean;
    sendEventRequest?: boolean;
}

/**
 * Renders an asset popup for asset markers. Fetches data when rendered.
 * @param assetId the id of the asset to fetch data for
 * @param date the date to fetch data for (YYYY-MM-DD)
 * @param uomPref the user's selected UnitOfMeasurement
 * @param recordId the record ID to fetch data with
 * @param dvrImei the IMEI of the DVR, enables DVR quick action buttons
 * @param hideQuickButtons toggle visibility of the quick action buttons
 * @param sendEventRequest send the event toggle with the API request
 */
const AssetPopup: React.FC<AssetPopupProps> = ({
    assetId,
    date,
    uomPref,
    recordId,
    dvrImei = '',
    hideQuickButtons = false,
    sendEventRequest = false,
}) => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.user as any);
    const liveVideoAvailable = user?.permissions.historic_video_downloading === true && dvrImei !== "";
    const videoSearchAvailable = user?.permissions.live_video === true && assetId && dvrImei !== "";
    const eventsAvailable = user?.permissions?.event_searching;
    const [ data, setData ] = useState<AssetPopupResponse | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const actAsTracker = (data?.act_as_tracker || 0) === 1;
    const [ isFetching, setIsFetching ] = useState<boolean>(false);
    const [ error, setError ] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const reqIdRef = useRef(0);
    const cancelInFlight = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
    }, []);

    useEffect(() => {
        if (assetId && date) {
            cancelInFlight();
            abortRef.current = new AbortController();
            setError(null);
            setData(null);
            setIsFetching(true);
            const requestId = ++reqIdRef.current;
            const params = { 
                record_id: recordId,
            } as any;
            if (sendEventRequest) {
                params.event = true;
            }
            fetchApiAuth({
                method: 'GET',
                url: 'device/location-detailed-info',
                params,
                signal: abortRef.current.signal,
            })
                .then((res: AxiosResponse<AssetPopupResponse>) => {
                    setData(res?.data?.marker ? res.data : null);
                    setIsFetching(false);
                    if (requestId === reqIdRef.current) {
                        setData(res?.data?.marker ? res.data : null);
                        setIsFetching(false);
                    }
                })
                .catch((err: any) => {
                    const aborted =
                        err?.name === 'AbortError' ||
                        err?.name === 'CanceledError' ||
                        err?.code === 'ERR_CANCELED';
                    if (!aborted) {
                        setError('Failed to fetch device data');
                    }
                    setIsFetching(false);
                    if (!aborted && requestId === reqIdRef.current) {
                        setError('Failed to fetch device data');
                        setIsFetching(false);
                    }
                });
        }
        return () => { 
            cancelInFlight(); 
        };
    }, [assetId, date, recordId, cancelInFlight]);

    if (error) {
        return (
            <div 
                className='asset-popup-error' 
                onMouseLeave={cancelInFlight}
            >
                {error}
            </div>
        );
    }

    if (isFetching) {
        return (
            <div onMouseLeave={cancelInFlight}>
                <Spinner 
                    loading
                    minWidth={320}
                    minHeight={200}
                    size={24}
                >
                    <div className='asset-popup-loading'>
                        &ensp;
                    </div>
                </Spinner>
            </div>
        );
    } 
    if (data) {
        return (
            <div 
                ref={rootRef}
                className='asset-popup-loaded' 
                onMouseLeave={cancelInFlight}
            >
                <Row>
                    <Col xs={3}>
                        <SpeedSign 
                            speed={data.marker.speed.toFixed(0)} 
                            isMetric={uomPref === UnitOfMeasurement.Metric || false}
                        />
                    </Col>
                    <Col xs={21}>
                        <h3 className='title'>{data.title}</h3>
                    </Col>
                    <Col xs={20}>
                        <div className='metadata'>
                            <div className='status'>
                                <div 
                                    className='status-icon' 
                                    style={{ backgroundColor: getStatusIconColor(data.online_status) }}
                                />
                                <span className="status-text">{data.online_status}</span>
                            </div>
                            <div className='driver-name'>
                                <Icon name='person' />
                                &ensp;
                                <a 
                                    href={`/drivers/view/${data.user_id}`} 
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {data.driver}
                                </a>
                            </div>
                            <div className='driver-score'>
                                <span className='fake-icon-percentage'>%</span>
                                &ensp;
                                Driver Score:
                                &thinsp;
                                <span style={{ fontWeight: 600 }}>{data.driver_score?.toFixed(0) || 0}</span>
                                %
                            </div>
                            <div className='lat-lng'>
                                <Icon name='location' />
                                &ensp;
                                <div>{data.marker.lat}, {data.marker.lng}</div>
                            </div>
                            <div className='address'>
                                <Icon name='road' />
                                &ensp;
                                {data.marker.location}
                            </div>
                        </div>
                    </Col>
                    <Col xs={4}>
                        {hideQuickButtons ? null : (
                            <div className="quick-actions">
                                <Tooltip 
                                    title={liveVideoAvailable ? "Live Video Feed" : "Asset does not have a DVR device"}
                                    getPopupContainer={() => rootRef.current ?? document.body}
                                    destroyTooltipOnHide
                                >
                                    <Button 
                                        className={`btn btn-empty ${liveVideoAvailable ? 'btn-quick-actions' : 'btn-quick-actions-disabled'}`}
                                        onClick={() => {
                                            if (!liveVideoAvailable) {
                                                return;
                                            }
                                            history.push(`/video/live-videos/${assetId}`);
                                        }}
                                        style={{ cursor: liveVideoAvailable ? 'pointer' : 'default' }}
                                    >
                                        <Video color={liveVideoAvailable ? 'var(--cool-gray-80)' : 'var(--cool-gray-40)'} />
                                    </Button>
                                </Tooltip>
                                <Tooltip 
                                    title={videoSearchAvailable ? 'Video Search' : 'Asset does not have a DVR device'}
                                    destroyTooltipOnHide
                                    getPopupContainer={() => rootRef.current ?? document.body}
                                >
                                    <Button 
                                        className={`btn btn-empty ${videoSearchAvailable ? 'btn-quick-actions' : 'btn-quick-actions-disabled'}`}
                                        onClick={() => {
                                            if (!videoSearchAvailable) {
                                                return;
                                            }
                                            dispatch(setVideoSearchFilters({
                                                page: 1,
                                                search: {
                                                    asset_id: assetId,
                                                    date,
                                                    time_from: '00:00',
                                                    time_to: '23:59',
                                                },
                                                order: {},
                                            }));
                                            history.push('/video/video-search');
                                        }}
                                        style={{ cursor: videoSearchAvailable ? 'pointer' : 'default' }}
                                    >
                                        <VideoChat color={videoSearchAvailable ? 'var(--cool-gray-80)' : 'var(--cool-gray-40)'} />
                                    </Button>
                                </Tooltip>
                                {eventsAvailable ? (
                                    <Tooltip 
                                        title={actAsTracker ? (
                                            <>
                                                Event history not available for this asset,
                                                &ensp;
                                                <a
                                                    href="https://www.fleetclear.com/contact"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Click Here
                                                </a>
                                                &ensp;
                                                to find out more
                                            </>
                                        ) : 'Event History'}
                                        destroyTooltipOnHide
                                        getPopupContainer={() => rootRef.current ?? document.body}
                                    >
                                        <Button 
                                            className={`btn btn-empty ${actAsTracker ? 'btn-quick-actions-disabled' : 'btn-quick-actions'}`}
                                            onClick={() => {
                                                if (actAsTracker) {
                                                    return;
                                                }
                                                dispatch(updateEventSearch({
                                                    assetId,
                                                    dateFrom: date,
                                                }));
                                                history.push('/events/search');
                                            }}
                                            style={{ cursor: actAsTracker ? 'default' : 'pointer' }}
                                        >
                                            <Notification color={actAsTracker ? 'var(--cool-gray-40)' : 'var(--cool-gray-80)'} />
                                        </Button>
                                    </Tooltip>
                                ) : null}
                                <Tooltip 
                                    title={actAsTracker ? (
                                        <>
                                            Timesheet report not available for this asset,
                                            &ensp;
                                            <a
                                                href="https://www.fleetclear.com/contact"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Click Here
                                            </a>
                                            &ensp;
                                            to find out more
                                        </>
                                    ) : 'Timesheet Report'}
                                    destroyTooltipOnHide
                                    getPopupContainer={() => rootRef.current ?? document.body}
                                >
                                    <Button 
                                        className={`btn btn-empty ${actAsTracker ? 'btn-quick-actions-disabled' : 'btn-quick-actions'}`}
                                        onClick={() => {
                                            if (actAsTracker) {
                                                return;
                                            }
                                            dispatch(getTimesheetReportCsvRequest({
                                                asset_id: [assetId],
                                                date_from: date,
                                                date_to: date,
                                                asset_or_driver: "assets",
                                                journey_or_day: "journeys",
                                            }));
                                        }}
                                        style={{ cursor: actAsTracker ? 'default' : 'pointer' }}
                                    >
                                        <CalendarHeatMap color={actAsTracker ? 'var(--cool-gray-40)' : 'var(--cool-gray-80)'} />
                                    </Button>
                                </Tooltip>
                            </div>
                        )}
                    </Col>
                    <Col xs={24}>
                        <div className='avl-table-container'>
                            <table>
                                <tbody>
                                    {data.marker.data.map((avlKey: AvlKey) => (
                                        <tr key={avlKey.value}>
                                            <td>&ensp;{avlKey.description}</td>
                                            <td>{avlKey.value}&ensp;</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }
    
    return (
        <div 
            className='asset-popup-error' 
            onMouseLeave={cancelInFlight}
        >
            Failed to fetch device data
        </div>
    );
}

export default React.memo(AssetPopup);